import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { CartApi } from 'api';
import type { Cart } from 'api/methods/CartApi';

/**
 * [Cart] Query key 생성
 * --
 */
export const cartQueryKeys = createQueryKeys('cart', {
  getCart: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getCarts: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [Cart] 장바구니 전체 조회
 * --
 */
export const useGetCarts = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: cartQueryKeys.getCarts(params).queryKey,
    queryFn: () => CartApi.getCarts(params),
    ...options,
  });
  return data;
};

/**
 * [Cart] 장바구니 상세 조회
 * --
 */
export const useGetCart = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: cartQueryKeys.getCart(idx, params).queryKey,
    queryFn: () => CartApi.getCart(idx, params),
  });

  return data;
};

/**
 * [Cart] 장바구니 생성
 * --
 */
export const useCreateCart = () => {
  const { mutateAsync: createCart, ...result } = useMutation({
    mutationFn: (variables: Cart) => CartApi.createCart(variables),
  });

  return { createCart, ...result };
};

/**
 * [Cart] 장바구니 수정
 * --
 */
export const useUpdateCart = () => {
  const { mutateAsync: updateCart, ...result } = useMutation({
    mutationFn: (variables: Cart) => CartApi.updateCart(variables),
  });

  return { updateCart, ...result };
};

/**
 * [Cart] 장바구니 삭제
 * --
 */
export const useDeleteCart = () => {
  const { mutateAsync: deleteCart, ...result } = useMutation({
    mutationFn: (variables: number) => CartApi.deleteCart(variables),
  });

  return { deleteCart, ...result };
};

/**
 * [Cart] 장바구니 Bulk 생성/삭제
 * --
 */
export const useUpsertBulkCart = () => {
  const { mutateAsync: upsertBulkCart, ...result } = useMutation({
    mutationFn: (variables: Cart[]) => CartApi.upsertBulkCart(variables),
  });

  return { upsertBulkCart, ...result };
};

/**
 * [Cart] 장바구니 Bulk 삭제
 * --
 */
export const useBulkDeleteCart = () => {
  const { mutateAsync: bulkDeleteCart, ...result } = useMutation({
    mutationFn: (variables: Cart[]) => CartApi.bulkDeleteCart(variables),
  });

  return { bulkDeleteCart, ...result };
};
